import React, { useContext, useEffect, useState } from 'react';
import { Box, Expandable } from '@warp-ds/react';
import Takeout, { TakeoutProps } from './takeout.js';
import Person from '../icons/person.js';
import Category from './category.js';
import { SchibstedPrivacySettingsContext } from '../schibsted-privacy-settings-context.js';
import { onGDPRInfoSchibsted } from '../schibsted-privacy-settings-tracking.js';
import { Trans, useLingui } from '@lingui/react';
import TranslationContext from '../translation-context.js';
import { Scope } from '../../scope.js';
import Logo from '../icons/logo.js';

export type SchibstedPrivacySettingsPageProps = {
    consentUser: ConsentUser;
} & TakeoutProps;

export default function SchibstedPrivacySettings({
    consentUser,
    links,
}: SchibstedPrivacySettingsPageProps): React.ReactElement {
    const { i18n } = useLingui();
    const { texts } = useContext(TranslationContext);
    const ctx = useContext(SchibstedPrivacySettingsContext);

    const [showSchibstedPrivacySetting, setShowSchibstedPrivacySetting] =
        useState(true);

    const showBrandPrivacySettings =
        !!ctx.brandPrivacySettings?.objectionCategories.length;

    useEffect(() => {
        // Hide privacy settings for anonymous users if the environmentId is not set.
        // This needs to updated with a state change.
        if (!consentUser.isLoggedIn && !ctx.environmentId) {
            setShowSchibstedPrivacySetting(false);
        }
        if (
            showSchibstedPrivacySetting &&
            (ctx.privacySettings?.objectionCategories.length || 0) < 1 &&
            ctx.environmentId
        ) {
            ctx.fetchPrivacySettings();
        }
    }, []);

    return (
        <div className="md:grid grid-cols-3 gap-32 space-y-32">
            <div className="md:col-span-2">
                {(showBrandPrivacySettings && (
                    <div className="flex">
                        <div className="grow">
                            <h2>
                                <Trans
                                    id="privacy-settings.consent.header"
                                    values={texts}
                                />
                            </h2>
                            <p>
                                <Trans
                                    id="privacy-settings.consent.paragraph"
                                    values={texts}
                                />
                            </p>
                        </div>
                        <div>
                            <Logo width="83px" />
                        </div>
                    </div>
                )) ||
                    ''}
                {ctx.brandPrivacySettings?.consentCategories?.map(
                    (category, index, map) => (
                        <Category
                            key={category.categoryTitle}
                            scope={Scope.BRAND}
                            category={category}
                            isLast={index === map.length - 1}
                        />
                    ),
                )}
                {ctx.brandPrivacySettings?.objectionCategories.map(
                    (category, index, map) => (
                        <Category
                            key={category.categoryTitle}
                            scope={Scope.BRAND}
                            category={category}
                            isLast={index === map.length - 1}
                        />
                    ),
                )}
                <div className="flex">
                    <div className="grow">
                        <h2>
                            <Trans
                                id="privacy-settings.schibsted.header"
                                values={texts}
                            />
                        </h2>
                        <p>
                            <Trans
                                id="privacy-settings.schibsted.paragraph"
                                values={texts}
                            />
                        </p>
                    </div>
                </div>
                {ctx.privacySettings?.objectionCategories.map(
                    (category, index, map) => (
                        <Category
                            key={category.categoryTitle}
                            scope={Scope.GLOBAL}
                            category={category}
                            isLast={index === map.length - 1}
                        />
                    ),
                )}
                <div className="my-32 s-border border-1 rounded-8 s-bg-info-subtle p-16">
                    <Expandable
                        title={
                            <span className="t4">
                                <Trans
                                    id="privacy-settings.external-ads.header"
                                    values={texts}
                                />
                            </span>
                        }
                        animated
                        buttonClass="w-full"
                        contentClass="mt-12"
                    >
                        <div>
                            <p className="text-s">
                                <Trans
                                    id="privacy-settings.external-ads.paragraph1"
                                    values={texts}
                                />
                            </p>
                            <p className="text-s">
                                <Trans
                                    id="privacy-settings.external-ads.paragraph2"
                                    values={texts}
                                />
                            </p>
                        </div>
                    </Expandable>
                </div>
                {consentUser.isLoggedIn && <Takeout links={links} />}
                <div>
                    {(consentUser.isLoggedIn && (
                        <a
                            target="_blank"
                            href={links.schibstedInfo as string}
                            onClick={onGDPRInfoSchibsted}
                        >
                            <Trans
                                id="privacy-settings.schibsted.info-link"
                                values={texts}
                            />
                        </a>
                    )) || (
                        <p>
                            <Trans
                                id="privacy-settings.schibsted.info"
                                values={texts}
                            />{' '}
                            <a
                                target="_blank"
                                href={links.schibstedInfo as string}
                                onClick={onGDPRInfoSchibsted}
                            >
                                <Trans
                                    id="privacy-settings.schibsted.info-link"
                                    values={texts}
                                />
                            </a>
                        </p>
                    )}
                </div>
            </div>
            <div className="md:col-span-1">
                <Box
                    className="s-bg-info-subtle hover:s-bg-info-subtle-hover s-border-info-subtle hover:s-border-info-subtle-hover border-2"
                    bleed
                >
                    <div className="text-center">
                        <Person className="block mx-auto my-16" />
                        <h2 className="text-l">
                            <Trans
                                id="privacy-settings.did-you-know.header"
                                values={texts}
                            />
                        </h2>
                        <p>
                            <Trans
                                id="privacy-settings.did-you-know.paragraph"
                                values={texts}
                            />
                            <br />
                            <a href={`${links.accountTipsLink}`}>
                                <Trans
                                    id="privacy-settings.did-you-know.link"
                                    values={texts}
                                />
                            </a>
                        </p>
                    </div>
                </Box>
            </div>
        </div>
    );
}
